const FALLBACK_MIME_TYPE = 'application/octet-stream'

const mimeTypeToExtension: Record<string, string> = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/gif': 'gif',
  'image/webp': 'webp',
  'image/svg+xml': 'svg',
  'audio/mpeg': 'mp3',
  'audio/wav': 'wav',
  'audio/ogg': 'ogg',
  'video/mp4': 'mp4',
  'video/webm': 'webm',
  'text/plain': 'txt',
  'text/markdown': 'md',
  'application/json': 'json',
  'application/yaml': 'yaml',
  'application/x-yaml': 'yaml',
  'application/xml': 'xml',
  'text/xml': 'xml',
  'application/javascript': 'js',
  'text/html': 'html',
  'text/css': 'css',
  'text/csv': 'csv',
}

const startsWith = (bytes: Uint8Array, signature: number[], offset = 0): boolean =>
  signature.every((byte, index) => bytes[offset + index] === byte)

const asciiAt = (bytes: Uint8Array, offset: number, text: string): boolean =>
  startsWith(
    bytes,
    [...text].map((c) => c.charCodeAt(0)),
    offset
  )

const sniffMimeType = (bytes: Uint8Array): string | undefined => {
  if (startsWith(bytes, [0x89, 0x50, 0x4e, 0x47])) return 'image/png'
  if (startsWith(bytes, [0xff, 0xd8, 0xff])) return 'image/jpeg'
  if (asciiAt(bytes, 0, 'GIF8')) return 'image/gif'
  if (asciiAt(bytes, 0, 'RIFF') && asciiAt(bytes, 8, 'WEBP')) return 'image/webp'
  if (asciiAt(bytes, 0, 'RIFF') && asciiAt(bytes, 8, 'WAVE')) return 'audio/wav'
  if (asciiAt(bytes, 0, 'ID3') || startsWith(bytes, [0xff, 0xfb])) return 'audio/mpeg'
  if (asciiAt(bytes, 0, 'OggS')) return 'audio/ogg'
  if (asciiAt(bytes, 4, 'ftyp')) return 'video/mp4'
  if (startsWith(bytes, [0x1a, 0x45, 0xdf, 0xa3])) return 'video/webm'
  return undefined
}

export const resolveMimeType = async (file: File): Promise<string> => {
  if (file.type) return file.type
  const header = new Uint8Array(await file.slice(0, 16).arrayBuffer())
  return sniffMimeType(header) ?? FALLBACK_MIME_TYPE
}

const structuredSuffixToExtension: Record<string, string> = {
  yaml: 'yaml',
  json: 'json',
  xml: 'xml',
  zip: 'zip',
}

export const extensionForMimeType = (mimeType: string, fileName: string): string => {
  const baseMimeType = mimeType.split(';')[0].trim().toLowerCase()
  const known = mimeTypeToExtension[baseMimeType]
  if (known) return known
  const suffix = baseMimeType.includes('+') ? baseMimeType.split('+').pop() : undefined
  const fromSuffix = suffix && structuredSuffixToExtension[suffix]
  if (fromSuffix) return fromSuffix
  const fromName = fileName.includes('.') ? fileName.split('.').pop() : undefined
  return fromName && fromName.length > 0 ? fromName.toLowerCase() : 'bin'
}
